/**
 * Activity Dashboard Page (route + file name stay `activity-report` — bookmarks,
 * help targets and UAT specs depend on them; only the label was renamed)
 *
 * A velocity dashboard over the user's job cards: how many jobs were added
 * in a period, and how far each travelled down the pipeline (generated →
 * applied → interviewing → offer) — presented as a funnel, milestone trend
 * lines, and a "where they are now" distribution.
 *
 * Data source: the existing GET /jobs list response. Each job carries a
 * `phase_history` list ({ts, phase}) appended by the backend on every phase
 * change; jobs that predate tracking are estimated from createdAt + their
 * current phase (a footnote appears when any job in view is estimated).
 * Everything is computed client-side — no dedicated endpoint.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Search,
  Table as TableIcon,
  X,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { api } from '../services/api';
import { PHASE_COLORS } from '../constants';
import type { UserJob, PhaseHistoryEntry } from '../types';

// ---------------------------------------------------------------------------
// Funnel model
// ---------------------------------------------------------------------------

// A job reaching any phase in a stage's set counts as having passed that
// stage (a job that is Interviewing was necessarily generated and applied,
// even if an intermediate entry is missing from its history).
type StageKey = 'added' | 'generated' | 'applied' | 'interviewing' | 'accepted';

interface StageDef {
  key: StageKey;
  label: string;
  phases: string[] | null;
}

const STAGE_DEFS: StageDef[] = [
  { key: 'added', label: 'Added', phases: null },
  { key: 'generated', label: 'Generated', phases: ['Ready', 'Applied', 'Follow-Up', 'Interviewing', 'Declined', 'Accepted'] },
  { key: 'applied', label: 'Applied', phases: ['Applied', 'Follow-Up', 'Interviewing', 'Declined', 'Accepted'] },
  { key: 'interviewing', label: 'Interviewing', phases: ['Interviewing', 'Accepted'] },
  { key: 'accepted', label: 'Offer accepted', phases: ['Accepted'] },
];

// Ordinal single-hue ramp (light → dark with funnel depth). Validated with
// the dataviz palette checker: monotone lightness, ΔL ≥ 0.06 per step,
// light end ≥ 2:1 on white.
const FUNNEL_RAMP = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'];

// Categorical slots 1–3 (fixed order) for the milestone trend lines.
const SERIES = [
  { key: 'added', label: 'Added', color: '#2a78d6' },
  { key: 'applied', label: 'Applied', color: '#1baf7a' },
  { key: 'interviews', label: 'Interviews', color: '#eda100' },
] as const;

const PHASE_ORDER = [
  'Queued', 'Generating', 'Ready', 'Applied', 'Follow-Up', 'Interviewing',
  'Declined', 'Accepted', 'Skipped', 'Expired', 'Errored', 'Trash',
];

interface JobTimeline {
  job: UserJob;
  addedTs: number;
  estimated: boolean; // true when history was synthesized (legacy job)
  // first time each funnel stage was reached (ms epoch), by stage key
  reached: Partial<Record<StageKey, number>>;
}

/** Effective phase history: the real list when present, else a synthesis
 *  from createdAt + the current phase. Always ends at the current phase. */
function effectiveHistory(job: UserJob): { entries: PhaseHistoryEntry[]; estimated: boolean } {
  const real = Array.isArray(job.phase_history) ? job.phase_history.filter((e) => e && e.ts && e.phase) : [];
  const changedAt = job.phase_changed_at || job.updatedAt || job.createdAt;
  if (real.length > 0) {
    const entries = [...real];
    if (entries[entries.length - 1].phase !== job.jobphase && job.jobphase) {
      entries.push({ ts: changedAt, phase: job.jobphase });
    }
    return { entries, estimated: false };
  }
  const entries: PhaseHistoryEntry[] = [{ ts: job.createdAt, phase: 'Queued' }];
  if (job.jobphase && job.jobphase !== 'Queued') {
    entries.push({ ts: changedAt, phase: job.jobphase });
  }
  return { entries, estimated: true };
}

function buildTimeline(job: UserJob): JobTimeline {
  const { entries, estimated } = effectiveHistory(job);
  const addedTs = Date.parse(job.createdAt) || Date.now();
  const reached: Partial<Record<StageKey, number>> = { added: addedTs };
  for (const stage of STAGE_DEFS) {
    if (!stage.phases) continue;
    for (const e of entries) {
      if (stage.phases.includes(String(e.phase))) {
        const t = Date.parse(e.ts);
        if (!Number.isNaN(t)) {
          reached[stage.key] = Math.min(reached[stage.key] ?? Infinity, t);
        }
      }
    }
  }
  return { job, addedTs, estimated, reached };
}

// ---------------------------------------------------------------------------
// Date ranges & bucketing
// ---------------------------------------------------------------------------

type RangeKey = '7d' | '30d' | '90d' | 'all' | 'custom';

const RANGE_PRESETS: { key: RangeKey; label: string }[] = [
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

type Granularity = 'day' | 'week' | 'month';

function bucketStart(d: Date, g: Granularity): Date {
  const c = startOfDay(d);
  if (g === 'week') {
    const dow = (c.getDay() + 6) % 7; // Monday = 0
    c.setDate(c.getDate() - dow);
  } else if (g === 'month') {
    c.setDate(1);
  }
  return c;
}

function nextBucket(d: Date, g: Granularity): Date {
  const c = new Date(d);
  if (g === 'day') c.setDate(c.getDate() + 1);
  else if (g === 'week') c.setDate(c.getDate() + 7);
  else c.setMonth(c.getMonth() + 1);
  return c;
}

function bucketLabel(d: Date, g: Granularity): string {
  if (g === 'month') return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

function pct(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ActivityReport() {
  const [jobs, setJobs] = useState<UserJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [range, setRange] = useState<RangeKey>('30d');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showChartTable, setShowChartTable] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.listJobs(undefined, 'all');
        setJobs(resp.jobs || []);
      } catch (err) {
        console.error('Failed to load jobs for activity report', err);
        setErrorMsg('Could not load your jobs. Try again in a moment.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const timelines = useMemo(() => jobs.map(buildTimeline), [jobs]);

  // Resolve the selected window [startTs, endTs]
  const { startTs, endTs } = useMemo(() => {
    const now = new Date();
    if (range === 'custom' && customStart) {
      const s = startOfDay(new Date(customStart)).getTime();
      const e = customEnd
        ? startOfDay(new Date(customEnd)).getTime() + 86_399_999
        : now.getTime();
      return { startTs: s, endTs: Math.max(s, e) };
    }
    if (range === 'all') {
      const first = timelines.length
        ? Math.min(...timelines.map((t) => t.addedTs))
        : now.getTime() - 29 * 86_400_000;
      return { startTs: startOfDay(new Date(first)).getTime(), endTs: now.getTime() };
    }
    const days = range === '7d' ? 7 : range === '90d' ? 90 : 30;
    return {
      startTs: startOfDay(new Date(now.getTime() - (days - 1) * 86_400_000)).getTime(),
      endTs: now.getTime(),
    };
  }, [range, customStart, customEnd, timelines]);

  // Cohort: jobs ADDED inside the window (matching text filter), tracked
  // through today — "of the jobs I ingested in this period, how far did
  // they get?"
  const cohort = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return timelines.filter((t) => {
      if (t.addedTs < startTs || t.addedTs > endTs) return false;
      if (!q) return true;
      const hay = `${t.job.jobcompany || ''} ${t.job.jobtitle || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [timelines, startTs, endTs, searchQuery]);

  const hasEstimates = useMemo(() => cohort.some((t) => t.estimated && t.job.jobphase !== 'Queued'), [cohort]);

  // Funnel stage counts + the between-stage gap breakdown (current phase of
  // the jobs that reached the previous stage but not this one)
  const funnel = useMemo(() => {
    return STAGE_DEFS.map((stage, i) => {
      const inStage = cohort.filter((t) => t.reached[stage.key] !== undefined);
      let gap: { phase: string; count: number }[] = [];
      if (i > 0) {
        const prevKey = STAGE_DEFS[i - 1].key;
        const lost = cohort.filter(
          (t) => t.reached[prevKey] !== undefined && t.reached[stage.key] === undefined
        );
        const byPhase = new Map<string, number>();
        for (const t of lost) {
          const p = t.job.jobphase || 'Unknown';
          byPhase.set(p, (byPhase.get(p) || 0) + 1);
        }
        gap = [...byPhase.entries()]
          .map(([phase, count]) => ({ phase, count }))
          .sort((a, b) => b.count - a.count);
      }
      return { ...stage, count: inStage.length, gap };
    });
  }, [cohort]);

  const added = funnel[0]?.count || 0;

  // Milestone trend: events per bucket, for the cohort's jobs
  const granularity: Granularity = useMemo(() => {
    const days = Math.max(1, Math.ceil((endTs - startTs) / 86_400_000));
    return days <= 31 ? 'day' : days <= 180 ? 'week' : 'month';
  }, [startTs, endTs]);

  const chartData = useMemo(() => {
    const buckets: { key: number; label: string; added: number; applied: number; interviews: number }[] = [];
    const index = new Map<number, number>();
    for (let d = bucketStart(new Date(startTs), granularity); d.getTime() <= endTs; d = nextBucket(d, granularity)) {
      index.set(d.getTime(), buckets.length);
      buckets.push({ key: d.getTime(), label: bucketLabel(d, granularity), added: 0, applied: 0, interviews: 0 });
    }
    const drop = (ts: number | undefined, field: 'added' | 'applied' | 'interviews') => {
      if (ts === undefined || ts < startTs || ts > endTs) return;
      const k = bucketStart(new Date(ts), granularity).getTime();
      const i = index.get(k);
      if (i !== undefined) buckets[i][field] += 1;
    };
    for (const t of cohort) {
      drop(t.reached.added, 'added');
      drop(t.reached.applied, 'applied');
      drop(t.reached.interviewing, 'interviews');
    }
    return buckets;
  }, [cohort, startTs, endTs, granularity]);

  // Current phase distribution of the cohort
  const distribution = useMemo(() => {
    const byPhase = new Map<string, number>();
    for (const t of cohort) {
      const p = t.job.jobphase || 'Unknown';
      byPhase.set(p, (byPhase.get(p) || 0) + 1);
    }
    const ordered = PHASE_ORDER.filter((p) => byPhase.has(p)).map((p) => ({ phase: p, count: byPhase.get(p)! }));
    const extras = [...byPhase.keys()].filter((p) => !PHASE_ORDER.includes(p)).map((p) => ({ phase: p, count: byPhase.get(p)! }));
    return [...ordered, ...extras];
  }, [cohort]);

  const maxDistribution = Math.max(1, ...distribution.map((d) => d.count));

  const medianDaysToApply = useMemo(() => {
    const days = cohort
      .filter((t) => t.reached.applied !== undefined)
      .map((t) => (t.reached.applied! - t.addedTs) / 86_400_000);
    const m = median(days);
    return m === null ? null : Math.round(m * 10) / 10;
  }, [cohort]);

  const kpis = [
    { label: 'Jobs added', value: added, sub: 'in period' },
    { label: 'Generated', value: funnel[1]?.count ?? 0, sub: `${pct(funnel[1]?.count ?? 0, added)} of added` },
    { label: 'Applied', value: funnel[2]?.count ?? 0, sub: `${pct(funnel[2]?.count ?? 0, added)} of added` },
    { label: 'Interviews', value: funnel[3]?.count ?? 0, sub: `${pct(funnel[3]?.count ?? 0, funnel[2]?.count ?? 0)} of applied` },
    { label: 'Offers accepted', value: funnel[4]?.count ?? 0, sub: 'in period' },
    { label: 'Median days to apply', value: medianDaysToApply ?? '—', sub: 'added → applied' },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header — minimal, with a clear path back to the main app */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 gap-4">
            <a href="/" className="flex items-center space-x-2">
              <img src="/assets/skillsnap_logo.png" alt="SkillSnap" className="h-8 w-auto" />
              <span className="text-xl font-semibold text-primary-600">SkillSnap</span>
            </a>
            <Link
              to="/Ready"
              data-help-target="activity.backtojobs"
              className="flex items-center space-x-1.5 px-3 py-1.5 text-sm text-gray-700 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Jobs</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Activity Dashboard</h1>
          <p className="text-sm text-gray-700 leading-relaxed">
            Your job-search velocity: every job you add moves down a funnel — generated,
            applied, interviewing, offer. Pick a period to see how the jobs you added in it
            have progressed (tracked through today).
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Loading your activity…
          </div>
        ) : errorMsg ? (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 text-sm">{errorMsg}</div>
        ) : (
          <>
            {/* Filter row — scopes everything below it */}
            <div className="bg-white rounded-lg shadow p-3 mb-6 flex flex-wrap items-center gap-2" data-uat="activity-filters">
              <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden">
                {RANGE_PRESETS.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => setRange(r.key)}
                    data-uat={`activity-range-${r.key}`}
                    className={`px-3 py-1.5 text-sm transition-colors ${
                      range === r.key
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              {range === 'custom' && (
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    aria-label="Start date"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    aria-label="End date"
                  />
                </div>
              )}
              <div className="relative ml-auto min-w-[220px] flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter by company or title…"
                  aria-label="Filter jobs by company or title"
                  className="w-full pl-9 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear filter"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {added === 0 ? (
              <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500 text-sm">
                No jobs were added in this period{searchQuery ? ' (or none match your filter)' : ''}.
                Widen the date range, or add some jobs and come back.
              </div>
            ) : (
              <>
                {/* KPI row */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                  {kpis.map((k) => (
                    <div key={k.label} className="bg-white rounded-lg shadow p-4">
                      <div className="text-sm text-gray-600">{k.label}</div>
                      <div className="text-3xl font-semibold text-gray-900 mt-1">{k.value}</div>
                      <div className="text-xs text-gray-500 mt-1">{k.sub}</div>
                    </div>
                  ))}
                </div>

                {/* Funnel */}
                <div className="bg-white rounded-lg shadow p-6 mb-6" data-uat="activity-funnel">
                  <h2 className="text-lg font-semibold text-gray-900">Pipeline funnel</h2>
                  <p className="text-xs text-gray-500 mb-5">
                    Jobs added in the period, tracked through today. A job counts in every stage
                    it has ever reached.
                  </p>
                  <div className="space-y-1">
                    {funnel.map((stage, i) => {
                      const width = added > 0 ? Math.max(stage.count > 0 ? 2 : 0, (stage.count / added) * 100) : 0;
                      const prevCount = i > 0 ? funnel[i - 1].count : 0;
                      return (
                        <div key={stage.key}>
                          {i > 0 && (
                            <div className="flex items-center gap-2 pl-36 py-1 text-xs text-gray-500">
                              <span className="font-medium">{pct(stage.count, prevCount)} →</span>
                              {stage.gap.slice(0, 4).map((g) => (
                                <span
                                  key={g.phase}
                                  className={`px-1.5 py-0.5 rounded-full text-[11px] ${PHASE_COLORS[g.phase as keyof typeof PHASE_COLORS] || 'bg-gray-100 text-gray-800'}`}
                                >
                                  {g.count} {g.phase}
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="flex items-center gap-3">
                            <div className="w-32 text-sm text-gray-600 text-right shrink-0">{stage.label}</div>
                            <div className="flex-1 flex items-center gap-2 relative group" tabIndex={0}>
                              <div
                                className="h-6 rounded-r"
                                style={{ width: `${width}%`, backgroundColor: FUNNEL_RAMP[i], minWidth: stage.count > 0 ? 8 : 0 }}
                              />
                              <span className="text-sm font-semibold text-gray-900">{stage.count}</span>
                              <div className="hidden group-hover:block group-focus-within:block absolute left-0 -top-9 z-10 bg-gray-900 text-white text-xs rounded-lg px-3 py-1.5 whitespace-nowrap shadow-lg">
                                <span className="font-semibold">{stage.count}</span> {stage.label.toLowerCase()}
                                {i > 0 && <> · {pct(stage.count, prevCount)} of {funnel[i - 1].label.toLowerCase()}</>}
                                {i > 1 && <> · {pct(stage.count, added)} of added</>}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Table twin of the funnel */}
                  <table className="mt-6 w-full text-sm border-t border-gray-100">
                    <thead>
                      <tr className="text-left text-xs text-gray-500">
                        <th className="py-2 font-medium">Stage</th>
                        <th className="py-2 font-medium text-right">Jobs</th>
                        <th className="py-2 font-medium text-right">% of previous</th>
                        <th className="py-2 font-medium text-right">% of added</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.map((stage, i) => (
                        <tr key={stage.key} className="border-t border-gray-100">
                          <td className="py-1.5 text-gray-700">{stage.label}</td>
                          <td className="py-1.5 text-right tabular-nums text-gray-900">{stage.count}</td>
                          <td className="py-1.5 text-right tabular-nums text-gray-700">{i === 0 ? '—' : pct(stage.count, funnel[i - 1].count)}</td>
                          <td className="py-1.5 text-right tabular-nums text-gray-700">{pct(stage.count, added)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {hasEstimates && (
                    <p className="mt-3 text-xs text-gray-400">
                      Some jobs predate phase tracking; their journey is estimated from the
                      phase they're in today.
                    </p>
                  )}
                </div>

                {/* Trend + distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                  <div className="bg-white rounded-lg shadow p-6 lg:col-span-2" data-uat="activity-trend">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Milestones over time</h2>
                        <p className="text-xs text-gray-500 mb-4">
                          Jobs added, applications sent, and interviews reached per{' '}
                          {granularity === 'day' ? 'day' : granularity === 'week' ? 'week' : 'month'}.
                        </p>
                      </div>
                      <button
                        onClick={() => setShowChartTable((v) => !v)}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
                          showChartTable
                            ? 'bg-primary-50 border-primary-200 text-primary-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                        aria-pressed={showChartTable}
                      >
                        <TableIcon className="w-3.5 h-3.5" />
                        Table
                      </button>
                    </div>
                    {showChartTable ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500">
                              <th className="py-2 font-medium">{granularity === 'day' ? 'Day' : granularity === 'week' ? 'Week of' : 'Month'}</th>
                              {SERIES.map((s) => (
                                <th key={s.key} className="py-2 font-medium text-right">{s.label}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {chartData.map((b) => (
                              <tr key={b.key} className="border-t border-gray-100">
                                <td className="py-1.5 text-gray-700">{b.label}</td>
                                <td className="py-1.5 text-right tabular-nums text-gray-900">{b.added}</td>
                                <td className="py-1.5 text-right tabular-nums text-gray-900">{b.applied}</td>
                                <td className="py-1.5 text-right tabular-nums text-gray-900">{b.interviews}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: -18 }}>
                          <CartesianGrid stroke="#f0efec" vertical={false} />
                          <XAxis
                            dataKey="label"
                            tick={{ fontSize: 11, fill: '#898781' }}
                            axisLine={{ stroke: '#e1e0d9' }}
                            tickLine={false}
                            minTickGap={24}
                          />
                          <YAxis
                            allowDecimals={false}
                            tick={{ fontSize: 11, fill: '#898781' }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
                            labelStyle={{ color: '#111827', fontWeight: 600 }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} iconType="plainline" />
                          {SERIES.map((s) => (
                            <Line
                              key={s.key}
                              type="monotone"
                              dataKey={s.key}
                              name={s.label}
                              stroke={s.color}
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4, stroke: '#ffffff', strokeWidth: 2 }}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>

                  {/* Where those jobs are now */}
                  <div className="bg-white rounded-lg shadow p-6" data-uat="activity-distribution">
                    <h2 className="text-lg font-semibold text-gray-900">Where they are now</h2>
                    <p className="text-xs text-gray-500 mb-4">Current phase of the jobs added in the period.</p>
                    <div className="space-y-2">
                      {distribution.map((d) => (
                        <div key={d.phase} className="flex items-center gap-2">
                          <span
                            className={`w-24 shrink-0 text-center px-1.5 py-0.5 rounded-full text-[11px] ${PHASE_COLORS[d.phase as keyof typeof PHASE_COLORS] || 'bg-gray-100 text-gray-800'}`}
                          >
                            {d.phase}
                          </span>
                          <div className="flex-1 h-4 bg-gray-100 rounded-r overflow-hidden">
                            <div
                              className="h-4 rounded-r"
                              style={{ width: `${(d.count / maxDistribution) * 100}%`, backgroundColor: '#2a78d6', minWidth: 4 }}
                            />
                          </div>
                          <span className="w-8 text-right text-sm tabular-nums font-medium text-gray-900">{d.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
